import { Box } from "@mui/material";
import { type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

/**
 * Floating orb animations - smooth, opposite directions
 */
const animations = {
  "@keyframes floatOrb1": {
    "0%, 100%": {
      transform: "translate(-50%, -20%)",
    },
    "50%": {
      transform: "translate(50%, 20%)",
    },
  },
  "@keyframes floatOrb2": {
    "0%, 100%": {
      transform: "translate(30%, -30%)",
    },
    "50%": {
      transform: "translate(-30%, 30%)",
    },
  },
};

/**
 * Animated page wrapper with smooth wave background animations.
 * Warm peach/beige color theme.
 */
export function AnimatedBackgroundPageWrapper({ children }: Props) {
  return (
    <Box
      sx={{
        // Full-screen background
        position: "fixed",
        inset: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        // Warm peach gradient background
        background: `
          linear-gradient(135deg,
            rgba(255, 245, 235, 1) 0%,
            rgba(255, 235, 215, 1) 50%,
            rgba(255, 240, 225, 1) 100%
          )
        `,
        // Floating orb 1
        "&::before": {
          content: '""',
          position: "absolute",
          top: "-100%",
          left: "-100%",
          width: "300%",
          height: "300%",
          background: `radial-gradient(
            ellipse 800px 400px at 50% 50%,
            rgba(255, 200, 140, 0.6) 0%,
            rgba(245, 190, 130, 0.3) 40%,
            transparent 70%
          )`,
          animation: "floatOrb1 15s ease-in-out infinite",
          filter: "blur(60px)",
        },
        // Floating orb 2
        "&::after": {
          content: '""',
          position: "absolute",
          top: "-80%",
          left: "-80%",
          width: "260%",
          height: "260%",
          background: `radial-gradient(
            ellipse 600px 300px at 30% 70%,
            rgba(255, 220, 170, 0.6) 0%,
            rgba(250, 210, 160, 0.3) 50%,
            transparent 80%
          )`,
          animation: "floatOrb2 15s ease-in-out infinite",
          filter: "blur(60px)",
        },
        // Include animations
        ...animations,
        // Ensure content stays above orbs
        "& > *": {
          position: "relative",
          zIndex: 1,
        },
      }}
    >
      {children}
    </Box>
  );
}
